import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  useMediaQuery,
  Menu,
  MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { useNavigate, Navigate } from 'react-router-dom';
import { surface } from '../theme';
import VoiceOverConfigService from '../services/VoiceOverConfigService';
import AuthService from '../services/AuthService';
import MobileListCard from '../components/responsive/MobileListCard';

const EDGE_AVA_MULTILINGUAL_VOICE = 'en-US-AvaMultilingualNeural';

const PROVIDERS = [
  { value: 'j1', label: 'ElevenLabs' },
  { value: 'edge', label: 'Microsoft Edge TTS' },
];

function providerLabel(provider) {
  return PROVIDERS.find((p) => p.value === provider)?.label ?? 'ElevenLabs';
}

const initialForm = {
  name: '',
  provider: 'j1',
  voice_id: '',
  speed: 1,
};

function VoiceOverConfigsPage({
  scope = 'sleep-videos',
  backTo = '/sleep-videos',
  backLabel = 'Back to Sleep Videos',
  pageTitle = 'Voice Over Configs',
  emptyHint = 'No voice over configs yet. Create one to use when creating sleep videos.',
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState(null);
  const [mobileMenuId, setMobileMenuId] = useState(null);

  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const list = await VoiceOverConfigService.list(scope);
      setItems(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load voice over configs');
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm(initialForm);
    setFormError(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (config) => {
    setEditingId(config.id);
    setForm({
      name: config.name ?? '',
      provider: config.provider ?? 'j1',
      voice_id: config.voice_id ?? '',
      speed: config.speed ?? 1,
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (!saving) {
      setDialogOpen(false);
      setFormError(null);
    }
  };

  const handleFormChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setFormError(null);
  };

  const handleProviderChange = (e) => {
    const provider = e.target.value;
    setForm((prev) => ({
      ...prev,
      provider,
      // Ava Multilingual is the default Edge voice; clear it again when switching back to ElevenLabs.
      voice_id:
        provider === 'edge' && !prev.voice_id.trim()
          ? EDGE_AVA_MULTILINGUAL_VOICE
          : provider === 'j1' && prev.voice_id === EDGE_AVA_MULTILINGUAL_VOICE
            ? ''
            : prev.voice_id,
    }));
    setFormError(null);
  };

  const handleSubmit = async () => {
    const name = form.name?.trim();
    const voice_id = form.voice_id?.trim();
    if (!name) {
      setFormError('Name is required');
      return;
    }
    if (!voice_id) {
      setFormError('Voice ID is required');
      return;
    }
    const speed = Number(form.speed);
    if (!Number.isFinite(speed) || speed < 0.7 || speed > 1.2) {
      setFormError('Speed must be between 0.7 and 1.2');
      return;
    }
    try {
      setSaving(true);
      setFormError(null);
      if (editingId) {
        await VoiceOverConfigService.update(editingId, { ...form, speed }, scope);
      } else {
        await VoiceOverConfigService.create({ ...form, speed }, scope);
      }
      setDialogOpen(false);
      loadList();
    } catch (err) {
      setFormError(err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (id) => setDeleteId(id);
  const openMobileMenu = (event, id) => {
    event.stopPropagation();
    setMobileMenuAnchorEl(event.currentTarget);
    setMobileMenuId(id);
  };
  const closeMobileMenu = () => {
    setMobileMenuAnchorEl(null);
    setMobileMenuId(null);
  };
  const handleDeleteClose = () => {
    if (!deleting) setDeleteId(null);
  };
  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    try {
      setDeleting(true);
      await VoiceOverConfigService.delete(deleteId, scope);
      setDeleteId(null);
      loadList();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  if (!AuthService.hasRole(['admin', 'superadmin'])) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: 'white',
          mb: 3,
          textShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }}
      >
        {pageTitle}
      </Typography>

      <Paper sx={{ borderRadius: 2, background: surface.glass, boxShadow: 2 }}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(backTo)}
              sx={{ textTransform: 'none', width: { xs: '100%', sm: 'auto' } }}
            >
              {backLabel}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              sx={{ textTransform: 'none', fontWeight: 600, width: { xs: '100%', sm: 'auto' } }}
            >
              Create Config
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : isMobile ? (
            <Box>
              {items.length === 0 ? (
                <Typography color='text.secondary' sx={{ py: 3 }}>
                  {emptyHint}
                </Typography>
              ) : (
                items.map((config) => (
                  <MobileListCard
                    key={config.id}
                    title={config.name}
                    subtitle={`${providerLabel(config.provider)} · ${config.voice_id} · ${config.speed ?? 1}×`}
                    primaryAction={
                      <Button size='small' onClick={() => handleOpenEdit(config)} sx={{ minWidth: 0, px: 1 }}>
                        Edit
                      </Button>
                    }
                    secondaryAction={
                      <IconButton size='small' onClick={(e) => openMobileMenu(e, config.id)}>
                        <MoreVertIcon fontSize='small' />
                      </IconButton>
                    }
                  />
                ))
              )}
              <Menu anchorEl={mobileMenuAnchorEl} open={Boolean(mobileMenuAnchorEl)} onClose={closeMobileMenu}>
                <MenuItem
                  onClick={() => {
                    const target = items.find((it) => it.id === mobileMenuId);
                    if (target) handleOpenEdit(target);
                    closeMobileMenu();
                  }}
                >
                  Edit
                </MenuItem>
                <MenuItem
                  sx={{ color: 'error.main' }}
                  onClick={() => {
                    if (mobileMenuId) handleDeleteClick(mobileMenuId);
                    closeMobileMenu();
                  }}
                >
                  Delete
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Provider</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Voice ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Speed</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        {emptyHint}
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((config) => (
                      <TableRow key={config.id}>
                        <TableCell>{config.name}</TableCell>
                        <TableCell>{providerLabel(config.provider)}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {config.voice_id}
                        </TableCell>
                        <TableCell>{config.speed ?? 1}×</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEdit(config)}
                            title="Edit"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(config.id)}
                            title="Delete"
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? 'Edit Voice Over Config' : 'New Voice Over Config'}
        </DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError(null)}>
              {formError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Name"
            value={form.name}
            onChange={handleFormChange('name')}
            margin="normal"
            required
            placeholder="e.g. Calm narrator"
          />
          <TextField
            select
            fullWidth
            label="Provider"
            value={form.provider}
            onChange={handleProviderChange}
            margin="normal"
          >
            {PROVIDERS.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="Voice ID"
            value={form.voice_id}
            onChange={handleFormChange('voice_id')}
            margin="normal"
            required
            placeholder={form.provider === 'edge' ? EDGE_AVA_MULTILINGUAL_VOICE : 'Choose a voice ID'}
            helperText={
              form.provider === 'edge'
                ? `Edge voice name; ${EDGE_AVA_MULTILINGUAL_VOICE} is Ava Multilingual`
                : 'ElevenLabs voice ID'
            }
          />
          <TextField
            fullWidth
            label="Speed"
            type="number"
            value={form.speed}
            onChange={handleFormChange('speed')}
            margin="normal"
            inputProps={{ min: 0.7, max: 1.2, step: 0.05 }}
            helperText="0.7× to 1.2×; 1× is the original speed"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? 'Saving...' : editingId ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteId} onClose={handleDeleteClose}>
        <DialogTitle>Delete config?</DialogTitle>
        <DialogContent>
          This cannot be undone. Videos using this config will keep it until you change them.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : null}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default VoiceOverConfigsPage;
